/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import lodashMap from 'lodash/map';
import { Helmet } from 'react-helmet';

import defaultShareImage from '@images/lucid-air-pure-touring-grand-touring-1200x630.jpg';
import i18nContext from '@components/i18n/i18nContext';

function SEO({ description, meta, title, image, location, person }) {
  const { site } = {
    site: {
      siteMetadata: {
        title: 'Lucid Motors',
        description:
          'There were luxury cars, then EVs, and now there’s Lucid — the new generation of luxury electric.',
        author: '@lucid_motors',
        siteUrl: 'https://www.lucidmotors.com',
      },
    },
  };

  const { lng, direction, alternateUrls } = useContext(i18nContext);
  const metaDescription = description || site.siteMetadata.description;
  const domain = site.siteMetadata.siteUrl;

  // add www if siteUrl does not already have it
  const wwwDomain =
    domain.indexOf('www.') === -1
      ? domain.slice(0, 8) + 'www.' + domain.slice(8)
      : domain;

  // Remove domain trailing slash if it exists
  const wwwDomainNoSlash = wwwDomain.endsWith('/')
    ? wwwDomain.slice(0, -1)
    : wwwDomain;

  // Use image prop for the Share Image if it exists, otherwise use the default Share Image
  const shareImage = image || defaultShareImage;

  // Build full share image url
  const wwwShareImage = shareImage.startsWith('/')
    ? wwwDomainNoSlash + shareImage
    : wwwDomainNoSlash + '/' + shareImage;

  // Extract path if it exists
  const pageLocation = location?.pathname || '';
  const pageUrl = wwwDomainNoSlash + pageLocation;

  // Build the Canonical URL without language prefix
  const canonicalUrl = pageLocation.startsWith('/' + lng + '/')
    ? wwwDomainNoSlash + pageLocation.slice(3)
    : pageLocation.endsWith('/' + lng)
    ? wwwDomainNoSlash + pageLocation.slice(3)
    : pageUrl; // no language

  return (
    <Helmet
      htmlAttributes={{
        lang: lng,
        dir: direction,
      }}
      title={title}
      defer={false}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: `${title} | ${site.siteMetadata.title}`,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:url`,
          content: pageUrl,
        },
        {
          property: `og:image`,
          content: wwwShareImage,
        },
        {
          property: `og:image:width`,
          content: '1200',
        },
        {
          property: `og:image:height`,
          content: '630',
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          property: `fb:app_id`,
          content: `738121196241611`,
        },
      ].concat(meta)}
    >
      <link
        rel="canonical"
        href={
          canonicalUrl.endsWith('/') ? canonicalUrl.slice(0, -1) : canonicalUrl
        }
      />
      {lodashMap(alternateUrls, (url, langcode) => (
        <link
          key={langcode}
          rel="alternate"
          href={`${wwwDomainNoSlash}${
            url.endsWith('/') ? url.slice(0, -1) : url
          }`}
          hrefLang={langcode}
        />
      ))}
      {person ? (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Person',
            name: person.name,
            url: person.url,
            image: person.image,
            jobTitle: person.title,
            worksFor: {
              '@type': 'Organization',
              name: 'Lucid Motors',
            },
          })}
        </script>
      ) : (
        ''
      )}
    </Helmet>
  );
}

export default SEO;

SEO.defaultProps = {
  meta: [],
  description: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};
